import { SaSmsGuard } from '@components/sa-sms-messaging/sa-sms-guard';
import { SmsAbuseFlags } from '@components/sa-sms-messaging/sms-abuse-flags';
import { SmsAdminAuditTable } from '@components/sa-sms-messaging/sms-admin-audit-table';
import { SmsConfigAuditTable } from '@components/sa-sms-messaging/sms-config-audit-table';
import { SmsConsentLookup } from '@components/sa-sms-messaging/sms-consent-lookup';
import { SmsCreditsTab } from '@components/sa-sms-messaging/sms-credits-tab';
import { SmsDailyStatsChart } from '@components/sa-sms-messaging/sms-daily-stats-chart';
import { SmsLimitsForm } from '@components/sa-sms-messaging/sms-limits-form';
import { SmsSenderTimeline } from '@components/sa-sms-messaging/sms-sender-timeline';
import { SmsTenantToggles } from '@components/sa-sms-messaging/sms-tenant-toggles';
import { ISaSmsTenantDetail, SaSmsFeatures } from '@interfaces/sa-sms';
import { useNavigation } from '@refinedev/core';
import { Alert, Button, Col, Row, Spin, Statistic, Tabs } from 'antd';
import { useSaSmsErrorText, useSaSmsGet } from 'hooks/use-sa-sms-api';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

/** Super-admin SMS console: one tenant (toggles, limits, credits, sender / A2P, abuse flags, stats, audit). */
export const SaSmsTenantDetail: React.FC = () => {
    const { t, i18n } = useTranslation(['sa-sms']);
    const { id = '' } = useParams<{ id: string }>();
    const { push } = useNavigation();
    const errorText = useSaSmsErrorText();
    const [refreshKey, setRefreshKey] = useState(0);
    const { data, isLoading, isError, error, refetch } = useSaSmsGet<ISaSmsTenantDetail>(
        `/tenants/${id}`,
        undefined,
        !!id,
    );
    const { data: features } = useSaSmsGet<SaSmsFeatures>('/features');

    return (
        <SaSmsGuard>
            {(canEdit) => {
                if (isLoading) return <Spin style={{ display: 'block', margin: '64px auto' }} />;
                if (isError || !data)
                    return <Alert type="error" showIcon message={errorText(error)} />;
                const changed = () => {
                    refetch();
                    setRefreshKey((k) => k + 1);
                };
                const week = data.week;
                return (
                    <section className="sa-sms-page">
                        <Button type="link" onClick={() => push('/sms-messaging')}>
                            {t('detail.back')}
                        </Button>
                        <Row gutter={16} style={{ marginBottom: 16 }}>
                            <Col xs={12} md={6}>
                                <Statistic
                                    title={t('list.sent7d')}
                                    value={week.sent.toLocaleString(i18n.language)}
                                />
                            </Col>
                            <Col xs={12} md={6}>
                                <Statistic
                                    title={t('list.delivery')}
                                    value={week.deliveryPct ?? '-'}
                                    suffix={week.deliveryPct === null ? '' : '%'}
                                />
                            </Col>
                            <Col xs={12} md={6}>
                                <Statistic
                                    title={t('list.failure')}
                                    value={week.failurePct ?? '-'}
                                    suffix={week.failurePct === null ? '' : '%'}
                                />
                            </Col>
                            <Col xs={12} md={6}>
                                <Statistic
                                    title={t('credits.available')}
                                    value={
                                        data.balance?.available.toLocaleString(i18n.language) ?? '-'
                                    }
                                />
                            </Col>
                        </Row>
                        <Tabs
                            items={[
                                {
                                    key: 'overview',
                                    label: t('detail.overview'),
                                    children: (
                                        <Row gutter={[16, 16]}>
                                            <Col xs={24} lg={12}>
                                                <SmsTenantToggles
                                                    detail={data}
                                                    features={features}
                                                    canEdit={canEdit}
                                                    onChanged={changed}
                                                />
                                            </Col>
                                            <Col xs={24} lg={12}>
                                                <SmsLimitsForm
                                                    detail={data}
                                                    canEdit={canEdit}
                                                    onChanged={changed}
                                                />
                                            </Col>
                                            <Col xs={24} lg={12}>
                                                <SmsAbuseFlags
                                                    detail={data}
                                                    canEdit={canEdit}
                                                    onChanged={changed}
                                                />
                                            </Col>
                                            <Col xs={24} lg={12}>
                                                <SmsDailyStatsChart stats={data.stats} />
                                            </Col>
                                        </Row>
                                    ),
                                },
                                {
                                    key: 'credits',
                                    label: t('detail.credits'),
                                    children: (
                                        <SmsCreditsTab
                                            detail={data}
                                            canEdit={canEdit}
                                            onChanged={changed}
                                        />
                                    ),
                                },
                                {
                                    key: 'sender',
                                    label: t('detail.sender'),
                                    children: (
                                        <SmsSenderTimeline
                                            detail={data}
                                            canEdit={canEdit}
                                            onChanged={changed}
                                        />
                                    ),
                                },
                                {
                                    key: 'activity',
                                    label: t('detail.activity'),
                                    children: (
                                        <Row gutter={[16, 16]}>
                                            <Col span={24}>
                                                <SmsAdminAuditTable
                                                    tenantId={data.tenantId}
                                                    refreshKey={refreshKey}
                                                />
                                            </Col>
                                            {features?.configAudit && (
                                                <Col span={24}>
                                                    <SmsConfigAuditTable tenantId={data.tenantId} />
                                                </Col>
                                            )}
                                            {features?.consentLookup && (
                                                <Col span={24}>
                                                    <SmsConsentLookup tenantId={data.tenantId} />
                                                </Col>
                                            )}
                                        </Row>
                                    ),
                                },
                            ]}
                        />
                    </section>
                );
            }}
        </SaSmsGuard>
    );
};
